import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../../0_context/AppContext.js';
import KakaoAd from '../kakaoads.js';
import { TIER_LEVELS } from './TIER_LEVELS.js';

class KakaoAdErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error('[KakaoAd] render/load error:', error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const safeGetStorage = (key) => {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage?.getItem(key) ?? null
      : null;
  } catch (error) {
    console.warn('[PlayerPage] localStorage get failed:', error);
    return null;
  }
};

const safeSetStorage = (key, value) => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(key, value);
    }
  } catch (error) {
    console.warn('[PlayerPage] localStorage set failed:', error);
  }
};

export const PlayerPage = ({ isActive, PlayerPageLoginTap }) => {
  
  const context = useAppContext() || {};
  const { playerData: rawPlayerData, authStatus } = context;

  const [activeTab, setActiveTab] = useState('history');
  const [showSeasonPopup, setShowSeasonPopup] = useState(false);

  const savedYearMonth = safeGetStorage('hideSeasonPopup');

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYearMonth = `${today.getFullYear()}-${currentMonth}`;

  const isFirstDay =
    today.getDate() === 1 || savedYearMonth == null;

  useEffect(() => {
    if (isFirstDay) {
      if (savedYearMonth !== currentYearMonth) {
        setShowSeasonPopup(true);
      }
    }
  }, [
    isFirstDay,
    currentYearMonth,
    savedYearMonth
  ]);

  const handleClosePopup = () => {
    setShowSeasonPopup(false);
  };

  const handleNeverShowAgain = () => {
    safeSetStorage(
      'hideSeasonPopup',
      currentYearMonth
    );

    setShowSeasonPopup(false);
  };

  const aiHistoryList = useMemo(() => {
    if (authStatus == null || authStatus === "") return [];

    const playerData =
      rawPlayerData && typeof rawPlayerData === 'object'
        ? rawPlayerData
        : {};

    const list = [];

    Object.keys(playerData).forEach((dayKey) => {
      const item = playerData[dayKey];

      if (item && typeof item === 'object') {
        list.push({
          id: `ai-history-${dayKey}`,
          date:
            item.date ||
            `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${dayKey}`,
          common: item.common || '',
          oneline: item.oneline || '',
          rank: Number(item.rank) || 0,
          next: item.next || '',
        });
      }
    });

    return list.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [rawPlayerData, authStatus]);

  if (!isActive) {
    return null;
  }

  if (
    authStatus == null ||
    authStatus === '' ||
    authStatus !== 'google'
  ) {
    return (
      <section
        id="page-player"
        className="page active p-4 md:p-6 mb-24 animate-fadeIn max-w-3xl mx-auto space-y-6"
      >
        <div className="flex items-center justify-between pt-2">
          <div>
            <span className="text-xs font-bold text-indigo-600 tracking-wider uppercase">
              Player
            </span>

            <h2 className="text-2xl font-black text-slate-900">
              성장 기록
            </h2>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center space-y-4 min-h-[300px]">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100">
            <span className="material-icons-round text-3xl text-slate-400">
              lock
            </span>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-black text-slate-800">
              로그인이 필요한 기능입니다
            </h3>

            <p className="text-xs font-medium text-slate-500 leading-relaxed">
              <br />
              <br />
              구글 로그인 이후 AI 평가를
              <br />
              성장 기록을 살펴 볼 수 있습니다.
              <br />
            </p>

            <button
              onClick={PlayerPageLoginTap}
              className="mt-4 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl text-sm font-bold transition-all shadow-md shadow-indigo-200 flex items-center justify-center gap-2"
            >
              <span className="material-icons-round text-[18px]">
                login
              </span>

              구글 로그인
            </button>
          </div>
        </div>
      </section>
    );
  }

  const totalScore = aiHistoryList.reduce(
    (acc, cur) =>
      acc +
      (Number.isFinite(cur.rank)
        ? cur.rank
        : 0),
    0
  );

  const tiers = Array.isArray(TIER_LEVELS)
    ? TIER_LEVELS.filter(Boolean)
    : [];

  const fallbackTier = {
    level: 1,
    title: 'BEGINNER',
    desc: 'AI 평가를 시작해 성장 기록을 쌓아보세요.',
    minScore: 0,
    maxScore: 0,
    color: 'from-slate-500 to-slate-700',
    icon: 'fitness_center',
    badgeColor: 'bg-slate-100 text-slate-600',
  };

  const safeTiers =
    tiers.length > 0
      ? tiers
      : [fallbackTier];

  const currentTierIndex = safeTiers.findIndex(
    (t) =>
      totalScore >=
        Number(t.minScore ?? 0) &&
      totalScore <=
        Number(
          t.maxScore ??
            Number.MAX_SAFE_INTEGER
        )
  );

  const currentTier =
    safeTiers[
      currentTierIndex >= 0
        ? currentTierIndex
        : 0
    ];

  const nextTier =
    safeTiers[currentTierIndex + 1] ||
    currentTier;

  const tierRange =
    Number(currentTier.maxScore ?? 0) -
    Number(currentTier.minScore ?? 0);

  const tierProgress =
    nextTier !== currentTier &&
    tierRange > 0
      ? Math.min(
          100,
          Math.max(
            0,
            (
              (totalScore -
                Number(
                  currentTier.minScore ?? 0
                )) /
              tierRange
            ) * 100
          )
        )
      : 100;

  return (
    <section
      id="page-player"
      className="page active p-4 md:p-6 mb-24 animate-fadeIn max-w-3xl mx-auto space-y-6 relative"
    >
      {/* 팝업 모달 UI */}
      {showSeasonPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-5 animate-fadeIn print-hide">
          <div className="bg-white rounded-[32px] w-full max-w-sm shadow-2xl overflow-hidden animate-slideUp">

            {/* 팝업 헤더 */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-8 text-center relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/20 rounded-full blur-xl"></div>

              <div className="relative z-10 flex flex-col items-center space-y-3">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/30 shadow-inner">
                  <span className="material-icons-round text-4xl text-white">
                    celebration
                  </span>
                </div>

                <h3 className="text-2xl font-black text-white tracking-tight">
                  {currentMonth}월 시즌 시작!
                </h3>
              </div>
            </div>
            <div className="p-6 text-center space-y-6">
              <p className="text-sm font-bold text-slate-600 leading-relaxed">
                새로운 한 달이 시작되었습니다.
                <br />
                이번 시즌도 목표를 향해 힘내세요! 💪
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleClosePopup}
                  className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-colors shadow-lg shadow-indigo-200"
                >
                  확인
                </button>

                <button
                  onClick={handleNeverShowAgain}
                  className="w-full py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-500 font-bold rounded-2xl transition-colors text-sm"
                >
                  다시 보지 않기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. 상단 타이틀 */}
      <div className="flex items-center justify-between pt-2">
        <div>
          <span className="text-xs font-bold text-indigo-600 tracking-wider uppercase">
            Player
          </span>

          <h2 className="text-2xl font-black text-slate-900">
            {currentMonth}월 성장 기록
          </h2>
        </div>

        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center gap-1">
          <span className="material-icons-round text-sm">
            stars
          </span>

          LV. {currentTier.level}
        </span>
      </div>

      {/* 2. 캐릭터 대시보드 */}
      <div
        className={`p-6 rounded-3xl bg-gradient-to-br ${currentTier.color} text-white shadow-lg relative overflow-hidden`}
      >
        <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row items-center gap-6 relative z-10">

          <div className="relative">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-inner">
              <span className="material-icons-round text-6xl text-white drop-shadow-md">
                {currentTier.icon}
              </span>
            </div>

            <span className="absolute -bottom-2 -right-2 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-slate-900/80 text-white border border-white/20">
              Lv. {currentTier.level}
            </span>
          </div>

          <div className="flex-1 text-center sm:text-left space-y-2">

            <h3 className="text-2xl font-extrabold">
              {currentTier.title}
            </h3>

            <p className="text-xs text-white/80 leading-relaxed max-w-md">
              {currentTier.desc}
            </p>

            <div className="pt-2 space-y-1.5">

              <div className="flex justify-between items-end text-xs">
                <span className="font-medium text-white/80">
                  현재 누적 점수
                </span>

                <span className="font-black text-sm">
                  {totalScore.toLocaleString()}

                  <span className="text-[10px] font-normal text-white/70">
                    {' '}
                    / {currentTier.maxScore} PT
                  </span>
                </span>
              </div>

              <div className="w-full h-3 bg-black/20 rounded-full overflow-hidden p-0.5 border border-white/10">
                <div
                  className="h-full bg-white rounded-full transition-all duration-500 ease-out shadow-sm"
                  style={{
                    width: `${tierProgress}%`,
                  }}
                />
              </div>

              
              <div className="text-[11px] text-right text-white/70 font-medium">
                다음 레벨까지{' '}
                <span className="font-bold text-white">
                  {Math.max(
                    0,
                    Number(
                      currentTier.maxScore ?? 0
                    ) -
                      totalScore +
                      1
                  ).toLocaleString()}{' '}
                  PT
                </span>{' '}
                남음
              </div>

            </div>
          </div>
        </div>

        <p className="text-right text-xs text-white/80 leading-relaxed max-w-md">
          매월 1일 초기화됩니다
        </p>
      </div>

      {/* 광고 영역 */}
      <section className="w-full flex justify-center print-hide">
        <KakaoAdErrorBoundary>
          <KakaoAd />
        </KakaoAdErrorBoundary>
      </section>

      {/* 3. 탭 버튼 */}
      <div className="flex bg-slate-200/60 p-1 rounded-2xl">
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
            activeTab === 'history'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <span className="material-icons-round text-base">
            history
          </span>

          일일 AI 평가 점수 테이블
        </button>
        <button
          onClick={() => setActiveTab('tiers')}
          className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
            activeTab === 'tiers'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <span className="material-icons-round text-base">
            military_tech
          </span>

          캐릭터 성장 티어표
        </button>
      </div>

      {/* 4-A. AI 평가 결과 */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm space-y-4">

          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h4 className="text-sm font-black text-slate-800 flex items-center gap-2">
              <span className="material-icons-round text-indigo-500 text-lg">
                fact_check
              </span>

              최근 일일 평가 & 획득 점수
            </h4>

            <span className="text-xs text-slate-400">
              총 {aiHistoryList.length}건의 기록
            </span>
          </div>

          <div className="space-y-3">

            {aiHistoryList.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs font-medium">
                아직 등록된 AI 평가 데이터가 없습니다.
              </div>
            ) : (
              aiHistoryList.map((item) => (
                <div
                  key={item.id}
                  className="p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-indigo-100 transition-all space-y-2"
                >

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold text-slate-500">
                      {item.date}
                    </span>

                    <div className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-xl font-black text-xs flex items-center gap-1 border border-indigo-100">
                      <span className="material-icons-round text-sm">
                        stars
                      </span>

                      +{item.rank} PT
                    </div>
                  </div>

                  {item.oneline && (
                    <div className="text-xs font-bold text-slate-900 bg-white p-2.5 rounded-xl border border-slate-100">
                      "{item.oneline}"
                    </div>
                  )}

                  {item.common && (
                    <p className="text-xs font-medium text-slate-600 leading-relaxed px-1">
                      {item.common}
                    </p>
                  )}

                  {item.next && (
                    <div className="pt-1 flex items-start gap-1.5 text-[11px] text-indigo-600 font-semibold bg-indigo-50/50 p-2 rounded-lg">

                      <span className="material-icons-round text-sm shrink-0">
                        tips_and_updates
                      </span>

                      <span>
                        다음 제안: {item.next}
                      </span>

                    </div>
                  )}

                </div>
              ))
            )}

          </div>
        </div>
      )}

      {/* 4-B. 티어표 */}
      {activeTab === 'tiers' && (
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm space-y-4">

          <div className="border-b border-slate-100 pb-3">
            <h4 className="text-sm font-black text-slate-800 flex items-center gap-2">

              <span className="material-icons-round text-amber-500 text-lg">
                emoji_events
              </span>

              캐릭터 및 타이틀 단계 안내
            </h4>

            <p className="text-xs text-slate-400 mt-0.5">
              AI 평가 점수를 차곡차곡 쌓아 다음 단계의 타이틀을 해금하세요.
            </p>
          </div>

          <div className="space-y-3">

            {safeTiers.map((tier) => {

              const isCurrent =
                tier.level ===
                currentTier.level;

              const isUnlocked =
                totalScore >=
                Number(
                  tier.minScore ?? 0
                );

              return (
                <div
                  key={tier.level}
                  className={`p-4 rounded-2xl border transition-all flex items-center gap-4 ${
                    isCurrent
                      ? 'border-indigo-500 bg-indigo-50/30 ring-2 ring-indigo-500/20'
                      : isUnlocked
                        ? 'border-slate-200 bg-slate-50/50'
                        : 'border-slate-100 bg-slate-50/30 opacity-60'
                  }`}
                >

                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0 bg-gradient-to-br ${tier.color}`}
                  >
                    <span className="material-icons-round text-2xl">
                      {tier.icon}
                    </span>
                  </div>

                  <div className="flex-1 space-y-1">

                    <div className="flex items-center gap-2">

                      <span
                        className={`text-[10px] font-black px-2 py-0.5 rounded-full ${tier.badgeColor}`}
                      >
                        LV. {tier.level}
                      </span>

                      <h5 className="text-xs font-black text-slate-900">
                        {tier.title}
                      </h5>

                      {isCurrent && (
                        <span className="text-[10px] font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-md ml-auto">
                          현재 단계
                        </span>
                      )}

                    </div>

                    <p className="text-[11px] text-slate-500">
                      {tier.desc}
                    </p>

                  </div>

                  <div className="text-right shrink-0">

                    <span className="text-[10px] font-bold text-slate-400 block">
                      달성 기준
                    </span>

                    <span className="text-xs font-black text-slate-700">
                      {Number(
                        tier.minScore ?? 0
                      ).toLocaleString()}{' '}
                      PT ~
                    </span>

                  </div>

                </div>
              );
            })}

          </div>
        </div>
      )}

    </section>
  );
};

export default PlayerPage;